'use client';
import React from 'react';
import Link from 'next/link';
import Image from 'next/image';

const steps = [
{
  num: '01',
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
      </svg>,
  title: 'Book Appointment Online',
  desc: 'Book your appointment easily by calling us or sending a WhatsApp message. Our team will confirm your slot and match you with the right specialist.',
  badge: 'Available 7 days a week, 8am–8pm'
},
{
  num: '02',
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>,
  title: 'We Match You with a Specialist',
  desc: "Our system pairs you with a physiotherapist who specialises in your condition and is local to you. You\'ll receive their profile and credentials.",
  badge: 'CMT Certified & MIAP Registered'
},
{
  num: '03',
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
      </svg>,
  title: 'Your Physio Arrives at Home',
  desc: 'Your therapist arrives with all necessary equipment. Your first session includes a full assessment, diagnosis, and personalised treatment plan.',
  badge: 'All equipment included'
},
{
  num: '04',
  icon:
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
      </svg>,
  title: 'Track Your Recovery Progress',
  desc: 'You receive a progress report and updated home exercise programme. Your therapist monitors your improvement and adjusts the plan accordingly.',
  badge: 'Progress reports after every session'
}];

export default function HowItWorksSection() {
  return (
    <section id="how-it-works" className="section-pad-lg overflow-hidden" style={{ backgroundColor: 'var(--background)' }}>
      <div className="mx-auto max-w-6xl px-6">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 items-end mb-16">
          <div className="space-y-4">
            <span className="service-tag">Simple Process</span>
            <h2 className="text-section-xl font-display font-bold" style={{ color: 'var(--foreground)' }}>
              From booking to{' '}
              <span className="font-display italic text-gradient-teal">recovery</span>, we handle everything
            </h2>
          </div>
          <div className="space-y-4">
            <p className="text-lg leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Most patients receive their first home visit within 24 hours of booking. No waiting rooms, no travel — just expert care where you need it most.
            </p>
            <Link href="/contact" className="btn-primary self-start inline-flex">
              Book Your First Session
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {steps?.map((step) =>
          <div key={step?.num} className="bento-card group hover:border-primary/30">
              <div className="flex items-start gap-5">
                <div className="shrink-0">
                  <span className="text-5xl font-display font-bold text-gradient-teal leading-none">{step?.num}</span>
                </div>
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--secondary)' }}>
                      <span style={{ color: 'var(--primary)' }}>{step?.icon}</span>
                    </div>
                    <h3 className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>{step?.title}</h3>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{step?.desc}</p>
                  <div className="flex items-center gap-1.5 pt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14" style={{ color: 'var(--primary)' }}>
                      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs font-medium" style={{ color: 'var(--primary)' }}>{step?.badge}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Banner Image */}
        <div className="mt-12 relative rounded-3xl overflow-hidden h-56">
          <Image
            alt="Physiotherapist conducting a home visit assessment with a patient, professional equipment visible, bright modern home"
            src="/assets/images/WhatsApp_Image_2026-08-05_at_10.59.55_PM__2_-1785951056544.jpeg"
            fill
            className="object-cover"
            sizes="100vw" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
          <div className="absolute inset-0 flex items-center px-10">
            <div className="max-w-md">
              <p className="text-3xl font-display font-bold italic text-white leading-tight">
                Expert physiotherapy, delivered to your home across Kochi, Kerala.
              </p>
              <p className="text-sm mt-3 font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>— Dr Physio, Kochi</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}